import Close from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import {
  Card,
  CardContent,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { getInvoicingDocuments, saveReadyForInvoicing } from 'controllers/invoicing/index'
import { InvoicingDto, VehicleReadyForInvoicingDto } from 'dtos/invoicing'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, clientSidePagination, formatDateMoment } from 'utils'
import InvoicingDocumentsDownloadLink from './InvoicingDocumentsDownloadLink'

interface ReadyForInvoicingCardProps {
  refreshInvoicing: (invoicing: InvoicingDto) => void
  readyForInvoicing: VehicleReadyForInvoicingDto[]
}

const readyForInvoicingSelectionCellSx = {
  boxSizing: 'border-box',
  maxWidth: 40,
  minWidth: 40,
  padding: '2px 4px !important',
  textAlign: 'left',
  verticalAlign: 'middle',
  width: 40
}

export default function ReadyForInvoicingCard({ refreshInvoicing, readyForInvoicing }: ReadyForInvoicingCardProps) {
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isSavingAndDownloading, setIsSavingAndDownloading] = useState<boolean>(false)
  const [isSavingAndSendingToQuickBooks, setIsSavingAndSendingToQuickBooks] = useState<boolean>(false)
  const [isSavingAndMarkingInvoiced, setIsSavingAndMarkingInvoiced] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof VehicleReadyForInvoicingDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [selected, setSelected] = useState<VehicleReadyForInvoicingDto[]>([])

  const orderedVehicles = [...readyForInvoicing].sort(clientSideOrderBy<VehicleReadyForInvoicingDto>(orderBy, order))
  const visibleVehicles = orderedVehicles.slice(...clientSidePagination(page, pageSize))
  const selectedVisibleCount = visibleVehicles.filter(vehicle => selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)).length
  const allVisibleSelected = visibleVehicles.length > 0 && selectedVisibleCount === visibleVehicles.length
  const selectAllTooltip = `${allVisibleSelected ? 'Deselect' : 'Select'} all vehicles on this page (${visibleVehicles.length})`
  const hasMultipleBuyerExporters = new Set(selected.map(vehicle => vehicle.buyerExporterCompanyId)).size > 1
  const canSendToQuickBooks = selected.length > 0 && !hasMultipleBuyerExporters
  const quickBooksBuyerExporterTooltip =
    'Unable to send selected vehicles to QuickBooks because they are associated with different Buyer/Exporters. All selected vehicles must be associated with the same Buyer/Exporter.'

  const onChangeSortByDirection = (propertyName: keyof VehicleReadyForInvoicingDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  useEffect(() => {
    setPage(0)
    setSelected([])
  }, [pageSize])

  useEffect(() => {
    setIsSubmitting(isSaving || isSavingAndDownloading || isSavingAndSendingToQuickBooks || isSavingAndMarkingInvoiced)
  }, [isSaving, isSavingAndDownloading, isSavingAndSendingToQuickBooks, isSavingAndMarkingInvoiced])

  useEffect(() => {
    setSelected(currentSelected =>
      currentSelected
        .filter(selectedVehicle => readyForInvoicing.some(vehicle => vehicle.vehicleGuid === selectedVehicle.vehicleGuid))
        .map(selectedVehicle => {
          const currentVehicle = readyForInvoicing.find(vehicle => vehicle.vehicleGuid === selectedVehicle.vehicleGuid)
          return currentVehicle ? { ...currentVehicle, ...selectedVehicle } : selectedVehicle
        })
    )
  }, [readyForInvoicing])

  const toggleSelectAll = () => {
    setSelected(currentSelected => {
      if (allVisibleSelected) {
        return currentSelected.filter(selectedVehicle => !visibleVehicles.some(vehicle => vehicle.vehicleGuid === selectedVehicle.vehicleGuid))
      }

      const selectedVehicleGuids = new Set(currentSelected.map(vehicle => vehicle.vehicleGuid))
      return [...currentSelected, ...visibleVehicles.filter(vehicle => !selectedVehicleGuids.has(vehicle.vehicleGuid))]
    })
  }

  const selectAllCheckbox = (
    <Checkbox
      checked={allVisibleSelected}
      disabled={isSubmitting || visibleVehicles.length === 0}
      id='ready-for-invoicing-select-all'
      indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
      inputProps={{ 'aria-label': selectAllTooltip }}
      onChange={toggleSelectAll}
      size='small'
    />
  )

  const sendToQuickBooksButton = (
    <LoadingButton
      disabled={isSubmitting || !canSendToQuickBooks}
      id='ready-for-invoicing-send-to-quickbooks'
      loading={isSavingAndSendingToQuickBooks}
      onClick={() => {
        setIsSavingAndSendingToQuickBooks(true)
        saveReadyForInvoicing(selected, true, false)
          .then(res => {
            enqueueSnackbar('Ready For Invoicing Successfully!', { variant: 'success' })
            refreshInvoicing(res.value)
            setSelected([])
            setPage(0)
          })
          .finally(() => setIsSavingAndSendingToQuickBooks(false))
      }}
      size='small'
      variant='contained'
    >
      SAVE AND SEND SELECTED VEHICLES TO QUICKBOOKS
    </LoadingButton>
  )

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} sm='auto' container alignItems='center' spacing={1}>
            <Grid item>
              <Typography fontWeight='bold' variant='h6'>
                Ready For Invoicing
              </Typography>
            </Grid>
            {selected.length > 0 && (
              <Grid item>
                <Chip
                  deleteIcon={
                    <Tooltip title='Clear all selections'>
                      <span>
                        <IconButton aria-label='Clear all selections' disabled={isSubmitting} id='ready-for-invoicing-clear-selection' size='small'>
                          <Close fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                  label={`${selected.length} selected`}
                  onDelete={() => setSelected([])}
                />
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table
                size='small'
                sx={theme => ({
                  tableLayout: 'auto',
                  '& .MuiTableCell-head': {
                    color: theme.palette.primary.main,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    lineHeight: 1.25,
                    py: 0.75,
                    px: 0.75,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    verticalAlign: 'bottom'
                  },
                  '& .MuiTableCell-body': {
                    fontSize: '9pt',
                    lineHeight: 1.35,
                    py: 0.5,
                    px: 0.75,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    verticalAlign: 'middle'
                  },
                  '& .MuiTableCell-paddingCheckbox': {
                    py: 0.25,
                    px: 0.5,
                    width: 40,
                    verticalAlign: 'middle'
                  },
                  '& .MuiTableCell-body .MuiOutlinedInput-root': {
                    minHeight: 28
                  },
                  '& .MuiTableCell-body .MuiInputBase-input': {
                    fontSize: '9pt',
                    py: 0.375,
                    px: 0.75
                  },
                  '& .MuiTablePagination-root': {
                    fontSize: '9pt'
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
                    fontSize: '9pt'
                  },
                  '& .MuiTablePagination-toolbar': {
                    minHeight: 40
                  }
                })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox' sx={readyForInvoicingSelectionCellSx}>
                      {isSubmitting || visibleVehicles.length === 0 ? selectAllCheckbox : <Tooltip title={selectAllTooltip}>{selectAllCheckbox}</Tooltip>}
                    </TableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    keyName='importerStockNumber'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Importer Stock #
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    keyName='buyerExporterCompany'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Buyer/Exporter
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='papsNumber' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    PAPS #
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    VIN
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Year
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Make
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto> keyName='model' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Model
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    disabled
                    keyName='vehicleGuid'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Invoicing Documents
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    keyName='readyToInvoiceDateTime'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Ready To Invoice
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    keyName='sentToQuickBooksDateTime'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Sent To QuickBooks
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleReadyForInvoicingDto>
                    disabled
                    keyName='quickBooksInvoiceNumber'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    QB Invoice #
                  </OrderableTableCell>

                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleVehicles.map((vehicle, index) => (
                      <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell padding='checkbox' sx={readyForInvoicingSelectionCellSx}>
                          <Checkbox
                            checked={selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)}
                            disabled={isSubmitting}
                            id={`ready-for-invoicing-select-${vehicle.vehicleGuid}`}
                            inputProps={{ 'aria-label': `Select vehicle ${vehicle.vin}` }}
                            onChange={() => {
                              setSelected(currentSelected =>
                                currentSelected.some(value => value.vehicleGuid === vehicle.vehicleGuid)
                                  ? currentSelected.filter(value => value.vehicleGuid !== vehicle.vehicleGuid)
                                  : [...currentSelected, vehicle]
                              )
                            }}
                            size='small'
                          />
                        </TableCell>

                        <TableCell>{vehicle.importerStockNumber}</TableCell>

                        <TableCell>{vehicle.buyerExporterCompany}</TableCell>

                        <TableCell>{vehicle.papsNumber}</TableCell>

                        <TableCell>{vehicle.vin}</TableCell>

                        <TableCell>{vehicle.year}</TableCell>

                        <TableCell>{vehicle.make}</TableCell>

                        <TableCell>{vehicle.model}</TableCell>

                        <TableCell>
                          <InvoicingDocumentsDownloadLink vehicleGuid={vehicle.vehicleGuid} />
                        </TableCell>

                        <TableCell>{vehicle.readyToInvoiceDateTime ? formatDateMoment(vehicle.readyToInvoiceDateTime) : '-'}</TableCell>

                        <TableCell>{vehicle.readyToInvoiceDateTime ? formatDateMoment(vehicle.sentToQuickBooksDateTime) : '-'}</TableCell>

                        <TableCell>
                          <TextField
                            disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                            id={`ready-for-invoicing-invoice-number-${vehicle.vehicleGuid}`}
                            inputProps={{ 'aria-label': `QB Invoice # for vehicle ${vehicle.vin}` }}
                            onChange={e =>
                              setSelected(currentSelected =>
                                currentSelected.map(v => (v.vehicleGuid === vehicle.vehicleGuid ? { ...v, quickBooksInvoiceNumber: e.target.value } : v))
                              )
                            }
                            size='small'
                            value={
                              selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].quickBooksInvoiceNumber
                                : vehicle.quickBooksInvoiceNumber ?? ''
                            }
                          />
                        </TableCell>

                      </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={readyForInvoicing.length ?? 0}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Ready For Invoicing:**

This list displays vehicles that have been marked as ready for invoicing.

**Download Invoicing Documents:**

Press "Download" in the Invoicing Documents column to generate and download a PDF for a specific vehicle.

Select multiple vehicles and press "Save And Download Invoicing Documents For Selected Vehicles" to generate a combined PDF.

The PDF includes 7501, Recall Repair Orders, and uploaded documents (FedEx, Replacement Label, Repairs, Vehicle Clean Up, Miscellaneous).

**Save Button:**

Saves any entered QB Invoice # to its respective vehicle record.

**Save And Send Selected Vehicles To QuickBooks Button:**

Populates the "Sent To QuickBooks" column with the current date and initiates the data transfer to QuickBooks Pro Desktop 2022.

**Save And Mark Selected Vehicles As Invoiced Button:**

Checks that all selected vehicles have a QB Invoice #.

If any QB Invoice # field is blank, the operation displays an error.

If all QB Invoice # fields are filled, the selected vehicles are moved to the Invoiced list.`}
            </GreenText>
          </Grid>

          <Grid item container alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSaving}
                onClick={() => {
                  setIsSaving(true)
                  saveReadyForInvoicing(selected, false, false)
                    .then(res => {
                      enqueueSnackbar('Ready For Invoicing Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSaving(false))
                }}
                size='small'
                variant='contained'
              >
                SAVE
              </LoadingButton>
            </Grid>

            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSavingAndDownloading}
                onClick={() => {
                  setIsSavingAndDownloading(true)
                  saveReadyForInvoicing(selected, false, false).then(res => {
                    enqueueSnackbar('Ready For Invoicing Successfully!', { variant: 'success' })
                    refreshInvoicing(res.value)
                    setSelected([])
                    setPage(0)
                    getInvoicingDocuments(selected.map(v => v.vehicleGuid)).finally(() => setIsSavingAndDownloading(false))
                  })
                }}
                size='small'
                variant='contained'
              >
                SAVE AND DOWNLOAD INVOICING DOCUMENTS FOR SELECTED VEHICLES
              </LoadingButton>
            </Grid>

            <Grid item>
              {hasMultipleBuyerExporters ? (
                <Tooltip arrow placement='top' title={quickBooksBuyerExporterTooltip}>
                  <span>{sendToQuickBooksButton}</span>
                </Tooltip>
              ) : (
                sendToQuickBooksButton
              )}
            </Grid>

            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSavingAndMarkingInvoiced}
                onClick={() => {
                  setIsSavingAndMarkingInvoiced(true)
                  saveReadyForInvoicing(selected, false, true)
                    .then(res => {
                      enqueueSnackbar('Ready For Invoicing Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSavingAndMarkingInvoiced(false))
                }}
                size='small'
                variant='contained'
              >
                SAVE AND MARK SELECTED VEHICLES AS INVOICED
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
